import React, { useState } from 'react'
import { updateApplication } from '@/api/tapApplications'
import { createNote } from '@/api/tapResources'
import { TapApplication } from '@/types/index'

// Feature #153 — editable summary + preferred employment type. Surfaced on the
// Summary tab during the Review stage so the reviewer can capture the pitch
// for the candidate and a preferred employment type without leaving the page.
interface SummaryEditorProps {
  application: TapApplication
  onUpdated: (app: TapApplication) => void
}

// Feature #229: canonical 4 employment types. See AssessmentTab.tsx for the
// rationale — legacy values still parse server-side, but the picker is
// restricted to these going forward.
const EMPLOYMENT_TYPES = ['Full Time', 'Part Time', 'Casual', 'Contractor']

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '8px',
  padding: '20px',
  marginBottom: '16px',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--color-dark-grey)',
  textTransform: 'uppercase',
  marginBottom: '4px',
}

const SummaryEditor: React.FC<SummaryEditorProps> = ({ application, onUpdated }) => {
  const [editing, setEditing] = useState(false)
  const [summary, setSummary] = useState(application.summary || '')
  const [employmentType, setEmploymentType] = useState(application.preferredEmploymentType || '')
  // Feature #244: Position Title moved here from the ApplicationDetailsEditor
  // card so it sits alongside the rest of the HR-Agent-filled review fields
  // (summary + preferred employment type). The field still maps to the same
  // ``position_title`` column on ``tap_application``; only the placement in
  // the UI changed.
  const [positionTitle, setPositionTitle] = useState(application.positionTitle || '')
  const [saving, setSaving] = useState(false)

  const reset = () => {
    setSummary(application.summary || '')
    setEmploymentType(application.preferredEmploymentType || '')
    setPositionTitle(application.positionTitle || '')
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const updated = await updateApplication(application.id, {
        summary: summary || undefined,
        preferredEmploymentType: employmentType || undefined,
        // Feature #244: send empty strings as undefined so a cleared input
        // leaves the column untouched rather than writing "".
        positionTitle: positionTitle.trim() || undefined,
      })
      // Feature #197: auto-create a note when the review summary is updated.
      if (summary && summary !== application.summary) {
        try {
          await createNote(application.id, `[Review Notes] ${summary}`, application.stage)
        } catch { /* note creation is supplementary — don't block the save */ }
      }
      onUpdated(updated)
      setEditing(false)
    } catch {
      alert('Failed to save changes')
    } finally {
      setSaving(false)
    }
  }

  if (!editing) {
    return (
      <div style={cardStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--color-black)' }}>
            Review Notes
          </h3>
          <button
            onClick={() => {
              reset()
              setEditing(true)
            }}
            style={{
              padding: '6px 12px',
              backgroundColor: 'var(--color-white)',
              color: 'var(--color-black)',
              border: '1px solid var(--color-light-grey)',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer',
              fontFamily: 'var(--font-family)',
            }}
          >
            Edit
          </button>
        </div>
        {/* Feature #244: Position Title sits at the top of the Review Notes
            block — first thing the HR Agent sees when filling out the
            review, since the rest of the fields (summary, preferred
            employment type) are about *how* the candidate fits the role. */}
        <div style={{ marginBottom: '12px' }}>
          <div style={labelStyle}>Position Title</div>
          <div style={{ fontSize: '14px', color: 'var(--color-black)' }}>
            {application.positionTitle || <span style={{ color: 'var(--color-dark-grey)' }}>—</span>}
          </div>
        </div>
        <div style={{ marginBottom: '12px' }}>
          <div style={labelStyle}>Summary</div>
          <div style={{ fontSize: '14px', color: 'var(--color-black)', whiteSpace: 'pre-wrap' }}>
            {application.summary || <span style={{ color: 'var(--color-dark-grey)' }}>No summary captured yet.</span>}
          </div>
        </div>
        <div>
          <div style={labelStyle}>Preferred Employment Type</div>
          <div style={{ fontSize: '14px', color: 'var(--color-black)' }}>
            {application.preferredEmploymentType || <span style={{ color: 'var(--color-dark-grey)' }}>—</span>}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div style={cardStyle}>
      <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600, color: 'var(--color-black)' }}>
        Review Notes
      </h3>
      {/* Feature #244: Position Title — first field in the edit form so the
          HR Agent fixes any auto-classification typo from the Typeform
          submission before recording the rest of the review. */}
      <div style={{ marginBottom: '12px' }}>
        <label style={labelStyle}>Position Title</label>
        <input
          type="text"
          value={positionTitle}
          onChange={(e) => setPositionTitle(e.target.value)}
          placeholder="e.g. Senior Data Analyst"
          style={inputStyle}
        />
      </div>
      <div style={{ marginBottom: '12px' }}>
        <label style={labelStyle}>Summary</label>
        <textarea
          value={summary}
          onChange={(e) => setSummary(e.target.value)}
          rows={5}
          placeholder="Capture a short pitch of why this candidate is worth progressing…"
          style={{ ...inputStyle, resize: 'vertical' }}
        />
      </div>
      <div style={{ marginBottom: '16px' }}>
        <label style={labelStyle}>Preferred Employment Type</label>
        <select
          value={employmentType}
          onChange={(e) => setEmploymentType(e.target.value)}
          style={{ ...inputStyle, backgroundColor: 'var(--color-white)', cursor: 'pointer' }}
        >
          <option value="">—</option>
          {EMPLOYMENT_TYPES.map((t) => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
      </div>
      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
        <button
          onClick={() => {
            reset()
            setEditing(false)
          }}
          disabled={saving}
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--color-white)',
            color: 'var(--color-black)',
            border: '1px solid var(--color-light-grey)',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 500,
            cursor: 'pointer',
            fontFamily: 'var(--font-family)',
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '8px 16px',
            backgroundColor: 'var(--color-magenta)',
            color: 'var(--color-white)',
            border: 'none',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: 500,
            cursor: saving ? 'not-allowed' : 'pointer',
            opacity: saving ? 0.6 : 1,
            fontFamily: 'var(--font-family)',
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  )
}

export default SummaryEditor
